import { useEffect, useState } from "react";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "@material-ui/core/Button";
import AddCircle from "@material-ui/icons/AddCircle";
import Dialog from "@material-ui/core/Dialog";
import Card from "@material-ui/core/Card";
import CardHeader from "@material-ui/core/CardHeader";
import Typography from "@material-ui/core/Typography";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import Grid from "@material-ui/core/Grid";
import { UseMutationResult } from "@tanstack/react-query";
import { schema } from "./schema";
import { useServiceFormStyles } from "./styles";
import { useAttendanceLocalStyles } from "../../../../../../../../../../../styles";
import { AttendanceLocalDepartmentGuiche } from "../../../../../../../../../../../../../@types/attendance-local-department-guiche";
import { AttendanceLocalDepartmentGuicheService } from "../../../../../../../../../../../../../@types/attendance-local-department-guiche-service";
import { AttendanceLocalDepartmentGuicheServiceCreateData } from "../../../../../../../../../../../../../services/api-tansquery/attendance-local/department/guiche/service/create";
import ServiceField from "./components/service-field";
import { AttendanceLocal } from "../../../../../../../../../../../../../@types/attendance-local";
import { AttendanceLocalDepartment } from "../../../../../../../../../../../../../@types/attendance-local-department";

type ServiceFormProps = {
    unidade_slug: AttendanceLocal["slug_unidade"]
    setor_unidade: AttendanceLocalDepartment["id"]
    guiche_id: AttendanceLocalDepartmentGuiche["id"]
    buttonTrigger?: React.ReactNode;
    mutation: UseMutationResult<
        AttendanceLocalDepartmentGuicheService,
        unknown,
        AttendanceLocalDepartmentGuicheServiceCreateData,
        unknown
    >
};

export default function ServiceForm({ buttonTrigger, mutation, ...fieldProps }: ServiceFormProps) {
    const [open, setOpen] = useState(false);
    const { classes } = useServiceFormStyles();
    const { classes: defaultClasses } = useAttendanceLocalStyles();
    const form = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        defaultValues: {
            guiche: fieldProps.guiche_id.toString(),
            servico: "",
        }
    });

    async function handleSubmit(data: z.infer<typeof schema>) {
        if (mutation.isLoading) {
            return;
        }

        await mutation.mutateAsync({
            guiche: Number(data.guiche),
            servico: Number(data.servico),
        });
    }

    useEffect(() => {
        if (mutation.isSuccess) {
            setOpen(false);
        }
    }, [mutation.isSuccess]);

    return (
        <>
            {buttonTrigger ? (
                <span onClick={() => setOpen(true)}>
                    {buttonTrigger}
                </span>
            ) : (
                <Button
                    startIcon={<AddCircle />}
                    className={defaultClasses.addButton}
                    onClick={() => setOpen(true)}
                >
                    Serviço
                </Button>
            )}
            <Dialog open={open}>
                <Card
                    component="form"
                    onSubmit={form.handleSubmit(handleSubmit)}
                >
                    <CardHeader
                        avatar={<AddCircle />}
                        className={classes.cardHeader}
                        title={<Typography variant="h5">Serviço</Typography>}
                    />
                    <CardContent
                        container
                        spacing={2}
                        component={Grid}
                        style={{ minWidth: 600 }}
                    >
                        <Grid item xs={12}>
                            <Controller
                                name="servico"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <ServiceField
                                        {...fieldProps}
                                        field={field}
                                        fieldState={fieldState}
                                        style={{ flex: 3 }}
                                    />
                                )}
                            />
                        </Grid>
                    </CardContent>
                    <CardActions className={classes.cardActions}>
                        <Button
                            className={classes.button}
                            onClick={() => setOpen(false)}
                            disabled={mutation.isLoading}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            className={defaultClasses.addButton}
                            disabled={mutation.isLoading}
                        >
                            {mutation.isLoading ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </CardActions>
                </Card>
            </Dialog>
        </>
    );
}
